import { useCallback, useState } from "react";
import type { ProcessorCreatePayload } from "@/customTypings/processor/Processor";
import type { ProcessorDeletedRestoreAvailableConflict } from "@/utils/processor/processorCreateConflict";
import {
    PROCESSOR_CREATE_CONFLICT,
    ProcessorCreateConflictError,
    isProcessorDeletedRestoreAvailableConflict,
} from "@/utils/processor/processorCreateConflict";
import { DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, showToastError, showToastSuccess } from "@/utils";

type useProcessorRestoreOnCreateConflictOptions<TDetail extends { id: string }> = {
    restoreDeletedWithCreateValues: (payload: ProcessorCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail) => void;
};

export function useProcessorRestoreOnCreateConflict<TDetail extends { id: string }>({
    restoreDeletedWithCreateValues,
    onRestored,
}: useProcessorRestoreOnCreateConflictOptions<TDetail>) {
    const [restoreConflict, setRestoreConflict] =
        useState<ProcessorDeletedRestoreAvailableConflict | null>(null);
    const [pendingRestoreForm, setPendingRestoreForm] = useState<ProcessorCreatePayload | null>(null);
    const [restoringDeleted, setRestoringDeleted] = useState(false);

    const handleCreateError = useCallback((err: unknown): boolean => {
        if (err instanceof ProcessorCreateConflictError) {
            if (
                isProcessorDeletedRestoreAvailableConflict(
                    err.conflict,
                    PROCESSOR_CREATE_CONFLICT.deletedRestoreAvailable
                )
            ) {
                setRestoreConflict(err.conflict);
                return true;
            }
        }
        return false;
    }, []);

    const clearRestoreConflict = useCallback(() => {
        setRestoreConflict(null);
        setPendingRestoreForm(null);
    }, []);

    const confirmRestore = useCallback(
        async (confirmed: boolean) => {
            if (!confirmed || !restoreConflict || !pendingRestoreForm) {
                clearRestoreConflict();
                return;
            }
            setRestoringDeleted(true);
            try {
                const restored = await restoreDeletedWithCreateValues({
                    id: restoreConflict.existingId,
                    ...pendingRestoreForm,
                });
                const label = restoreConflict.existingName;
                clearRestoreConflict();
                showToastSuccess(`"${label}" restored successfully!`);
                onRestored(restored);
            } catch {
                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
            } finally {
                setRestoringDeleted(false);
            }
        },
        [restoreConflict, pendingRestoreForm, restoreDeletedWithCreateValues, onRestored, clearRestoreConflict]
    );

    const restoreModalProps = {
        show: restoreConflict !== null && pendingRestoreForm !== null,
        title: "Restore deleted Processor?",
        body: restoreConflict
            ? "A deleted Processor matching these details already exists. Restore it using the details you entered?"
            : "",
        buttonSuccessText: restoringDeleted ? "Restoring…" : "Yes",
        buttonCancelText: "No" as const,
    };

    return {
        handleCreateError,
        setPendingRestoreForm,
        confirmRestore,
        restoreModalProps,
    };
}
